import React, { useEffect, useRef } from "react";
import "./cursor-spotlight.css";

/**
 * CursorSpotlight \u2014 a soft radial light that tracks the cursor
 * inside its host container.
 *
 * Implementation: writes the cursor's local position into two CSS
 * custom properties (--mx, --my) on the spotlight element, and uses
 * pointer-events: none so it never intercepts clicks. requestAnimationFrame
 * throttles the mousemove handler to one update per paint, so the
 * effect costs roughly nothing even on low-end devices.
 *
 * Usage:
 *   <div className="some-host" style={{ position: "relative" }}>
 *     <CursorSpotlight color="rgba(34, 211, 238, 0.35)" size={500} />
 *     ...content...
 *   </div>
 *
 * The host MUST be position: relative (or absolute/fixed) for the
 * spotlight to size to it correctly.
 */
const CursorSpotlight = ({
  color = "rgba(34, 211, 238, 0.28)",
  size = 600,
  parentSelector,
}) => {
  const ref = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const host = parentSelector
      ? document.querySelector(parentSelector)
      : el.parentElement;
    if (!host) return;

    const onMove = (e) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const rect = host.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        el.style.setProperty("--mx", `${x}px`);
        el.style.setProperty("--my", `${y}px`);
        el.style.setProperty("--opacity", "1");
      });
    };

    const onLeave = () => {
      el.style.setProperty("--opacity", "0");
    };

    host.addEventListener("mousemove", onMove);
    host.addEventListener("mouseleave", onLeave);
    return () => {
      host.removeEventListener("mousemove", onMove);
      host.removeEventListener("mouseleave", onLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [parentSelector]);

  return (
    <div
      ref={ref}
      className="cursor-spotlight"
      aria-hidden="true"
      style={{
        "--spotlight-color": color,
        "--spotlight-size": `${size}px`,
      }}
    />
  );
};

export default CursorSpotlight;
